import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Archive,
  Upload,
  Download,
  Pencil,
  Trash2,
  Search,
  RefreshCw,
  FileText,
  FileSpreadsheet,
  File,
  Image,
  Database,
  CalendarDays,
  Building2,
  X,
  ExternalLink,
  Eye,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Constants ──────────────────────────────────────────────────────────────

const CATEGORIES = [
  "Bank Statements",
  "Payment Processor Statements",
  "Supplier Invoices",
  "Customer Invoices",
  "Receipts",
  "Tax Documents",
  "Contracts",
  "Company Documents",
  "Travel Supplier Agreements",
  "Other",
];

const PROVIDERS = [
  "Revolut", "Stripe", "Tikkie", "Amex", "Railway",
  "Cloudflare", "Google", "Meta", "Other",
];

const ACCEPT = ".pdf,.csv,.xlsx,.xls,.docx,.doc,.png,.jpg,.jpeg";
const MAX_BYTES = 25 * 1024 * 1024; // 25 MB

const CATEGORY_COLORS = {
  "Bank Statements":              "bg-blue-100 text-blue-700",
  "Payment Processor Statements": "bg-violet-100 text-violet-700",
  "Supplier Invoices":            "bg-amber-100 text-amber-800",
  "Customer Invoices":            "bg-emerald-100 text-emerald-700",
  "Receipts":                     "bg-teal-100 text-teal-700",
  "Tax Documents":                "bg-red-100 text-red-700",
  "Contracts":                    "bg-indigo-100 text-indigo-700",
  "Company Documents":            "bg-slate-200 text-slate-700",
  "Travel Supplier Agreements":   "bg-orange-100 text-orange-700",
  "Other":                        "bg-slate-100 text-slate-500",
};

// ── Utilities ──────────────────────────────────────────────────────────────

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024)           return `${bytes} B`;
  if (bytes < 1024 * 1024)   return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDate(str) {
  if (!str) return "—";
  try { return format(parseISO(str.slice(0, 10)), "d MMM yyyy"); }
  catch { return str; }
}

function fileIcon(mime) {
  if (!mime) return <File className="w-4 h-4 text-slate-400" />;
  if (mime === "application/pdf")                        return <FileText className="w-4 h-4 text-red-500" />;
  if (mime.includes("spreadsheet") || mime.includes("excel") || mime === "text/csv")
    return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
  if (mime.includes("word") || mime.includes("document")) return <FileText className="w-4 h-4 text-blue-500" />;
  if (mime.startsWith("image/"))                         return <Image className="w-4 h-4 text-violet-500" />;
  return <File className="w-4 h-4 text-slate-400" />;
}

const EMPTY_META = {
  title: "",
  category: "Bank Statements",
  provider: "",
  description: "",
  document_date: "",
  period_start: "",
  period_end: "",
  tags: "",
  related_invoice_number: "",
  related_booking_id: "",
  related_quote_number: "",
  notes: "",
};

// ── Upload Modal ───────────────────────────────────────────────────────────

function UploadModal({ open, onClose, onUploaded }) {
  const { getAuthHeaders } = useAuth();
  const fileRef  = useRef(null);
  const dropRef  = useRef(null);

  const [file,     setFile]     = useState(null);
  const [meta,     setMeta]     = useState(EMPTY_META);
  const [progress, setProgress] = useState(0);
  const [uploading,setUploading]= useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (open) { setFile(null); setMeta(EMPTY_META); setProgress(0); setUploading(false); }
  }, [open]);

  const set = (k, v) => setMeta(m => ({ ...m, [k]: v }));

  const validateAndSet = (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) { toast.error(`File too large — max 25 MB (this file is ${fmtSize(f.size)})`); return; }
    const ext = f.name.split(".").pop().toLowerCase();
    const allowed = ["pdf","csv","xlsx","xls","docx","doc","png","jpg","jpeg"];
    if (!allowed.includes(ext)) { toast.error(`File type .${ext} is not allowed`); return; }
    setFile(f);
    if (!meta.title) set("title", f.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " "));
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) validateAndSet(f);
  };

  const handleUpload = async () => {
    if (!file)       { toast.error("Please select a file"); return; }
    if (!meta.title) { toast.error("Title is required"); return; }
    if (!meta.category) { toast.error("Category is required"); return; }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      setUploading(true);
      try {
        const payload = {
          ...meta,
          tags: meta.tags ? meta.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
          file_data:  base64,
          file_name:  file.name,
          mime_type:  file.type || "application/octet-stream",
          file_size:  file.size,
        };
        await axios.post(`${API}/documents`, payload, {
          headers: getAuthHeaders(),
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        toast.success("Document uploaded");
        onUploaded();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        setProgress(0);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload Document
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* Drop zone */}
          <div
            ref={dropRef}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              dragOver ? "border-purple-400 bg-purple-50" : "border-slate-200 hover:border-purple-300 hover:bg-slate-50"
            }`}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={e => validateAndSet(e.target.files[0])}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                {fileIcon(file.type)}
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-800">{file.name}</p>
                  <p className="text-xs text-slate-400">{fmtSize(file.size)}</p>
                </div>
                <button onClick={e => { e.stopPropagation(); setFile(null); }} className="ml-auto text-slate-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div>
                <Archive className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Drag & drop or click to select</p>
                <p className="text-xs text-slate-400 mt-1">PDF, CSV, XLSX, DOCX, PNG, JPG — max 25 MB</p>
              </div>
            )}
          </div>

          {/* Upload progress */}
          {uploading && (
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Uploading…</span><span>{progress}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className="h-2 rounded-full bg-purple-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Title *</Label>
              <Input value={meta.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Revolut Statement June 2025" className="mt-1" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={meta.category} onValueChange={v => set("category", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={meta.provider || ""} onValueChange={v => set("provider", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select provider" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">— None —</SelectItem>
                  {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document Date</Label>
              <Input type="date" value={meta.document_date} onChange={e => set("document_date", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Period Start</Label>
              <Input type="date" value={meta.period_start} onChange={e => set("period_start", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Period End</Label>
              <Input type="date" value={meta.period_end} onChange={e => set("period_end", e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input value={meta.description} onChange={e => set("description", e.target.value)} placeholder="Brief description of this document" className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Tags <span className="text-slate-400 font-normal text-xs">(comma-separated)</span></Label>
              <Input value={meta.tags} onChange={e => set("tags", e.target.value)} placeholder="e.g. 2025, quarterly, export" className="mt-1" />
            </div>
          </div>

          {/* Linking */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Bookkeeping Links (optional)</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Invoice #</Label>
                <Input value={meta.related_invoice_number} onChange={e => set("related_invoice_number", e.target.value)} placeholder="TBF-INV-…" className="mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Booking ID</Label>
                <Input value={meta.related_booking_id} onChange={e => set("related_booking_id", e.target.value)} placeholder="Booking ID" className="mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Quote #</Label>
                <Input value={meta.related_quote_number} onChange={e => set("related_quote_number", e.target.value)} placeholder="Quote #" className="mt-1 text-sm" />
              </div>
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input value={meta.notes} onChange={e => set("notes", e.target.value)} placeholder="Internal notes about this document" className="mt-1" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancel</Button>
          <Button onClick={handleUpload} disabled={uploading || !file} className="bg-purple-600 hover:bg-purple-700 text-white">
            {uploading ? `Uploading ${progress}%…` : "Upload Document"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Metadata Modal ────────────────────────────────────────────────────

function EditModal({ open, onClose, onSaved, doc }) {
  const { getAuthHeaders } = useAuth();
  const [form,   setForm]   = useState(EMPTY_META);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && doc) {
      setForm({
        title:                  doc.title || "",
        category:               doc.category || "Other",
        provider:               doc.provider || "",
        description:            doc.description || "",
        document_date:          doc.document_date || "",
        period_start:           doc.period_start || "",
        period_end:             doc.period_end || "",
        tags:                   Array.isArray(doc.tags) ? doc.tags.join(", ") : (doc.tags || ""),
        related_invoice_number: doc.related_invoice_number || "",
        related_booking_id:     doc.related_booking_id || "",
        related_quote_number:   doc.related_quote_number || "",
        notes:                  doc.notes || "",
      });
    }
  }, [open, doc]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title)    { toast.error("Title is required"); return; }
    if (!form.category) { toast.error("Category is required"); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/documents/${doc.id}`, {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      }, { headers: getAuthHeaders() });
      toast.success("Metadata updated");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Document Metadata</DialogTitle>
        </DialogHeader>
        {doc && (
          <div className="mb-3 flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500">
            {fileIcon(doc.mime_type)}
            <span className="font-medium text-slate-700 truncate">{doc.file_name}</span>
            <span className="ml-auto shrink-0">{fmtSize(doc.file_size)}</span>
          </div>
        )}
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => set("title", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provider</Label>
              <Select value={form.provider || ""} onValueChange={v => set("provider", v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">— None —</SelectItem>
                  {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document Date</Label>
              <Input type="date" value={form.document_date} onChange={e => set("document_date", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Period Start</Label>
              <Input type="date" value={form.period_start} onChange={e => set("period_start", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Period End</Label>
              <Input type="date" value={form.period_end} onChange={e => set("period_end", e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => set("description", e.target.value)} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Tags <span className="text-slate-400 font-normal text-xs">(comma-separated)</span></Label>
              <Input value={form.tags} onChange={e => set("tags", e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Bookkeeping Links</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Invoice #</Label>
                <Input value={form.related_invoice_number} onChange={e => set("related_invoice_number", e.target.value)} placeholder="TBF-INV-…" className="mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Booking ID</Label>
                <Input value={form.related_booking_id} onChange={e => set("related_booking_id", e.target.value)} className="mt-1 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Quote #</Label>
                <Input value={form.related_quote_number} onChange={e => set("related_quote_number", e.target.value)} className="mt-1 text-sm" />
              </div>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={e => set("notes", e.target.value)} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Summary Strip ──────────────────────────────────────────────────────────

function SummaryStrip({ summary, loading }) {
  if (loading) return (
    <div className="flex items-center justify-center h-20">
      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
    </div>
  );
  if (!summary) return null;

  const cards = [
    { label: "Total Documents", value: summary.total_count,           icon: Archive,     color: "text-violet-600", bg: "bg-violet-50" },
    { label: "Storage Used",    value: fmtSize(summary.total_size),   icon: Database,    color: "text-blue-600",   bg: "bg-blue-50"   },
    { label: "Uploaded This Month", value: summary.month_count,       icon: CalendarDays,color: "text-emerald-600",bg: "bg-emerald-50"},
    { label: "Categories",      value: Object.keys(summary.by_category || {}).length, icon: Building2, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map(c => (
        <Card key={c.label}>
          <CardContent className="p-4">
            <div className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center mb-3`}>
              <c.icon className={`w-4 h-4 ${c.color}`} />
            </div>
            <p className="text-2xl font-bold text-slate-900">{c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Category Breakdown ─────────────────────────────────────────────────────

function CategoryBreakdown({ summary }) {
  if (!summary?.by_category || Object.keys(summary.by_category).length === 0) return null;
  const entries = Object.entries(summary.by_category).sort((a, b) => b[1].count - a[1].count);
  const maxCount = entries[0]?.[1]?.count || 1;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-600">Documents by Category</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          {entries.map(([cat, data]) => (
            <div key={cat}>
              <div className="flex justify-between text-sm mb-0.5">
                <span className="font-medium truncate">{cat}</span>
                <span className="text-slate-400 shrink-0 ml-2">{data.count} · {fmtSize(data.size)}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div
                  className="h-1.5 rounded-full bg-violet-400"
                  style={{ width: `${(data.count / maxCount) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function DocumentArchive() {
  const { getAuthHeaders } = useAuth();

  const [docs,        setDocs]        = useState([]);
  const [summary,     setSummary]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [sumLoading,  setSumLoading]  = useState(true);

  const [uploadOpen,  setUploadOpen]  = useState(false);
  const [editDoc,     setEditDoc]     = useState(null);
  const [deleteId,    setDeleteId]    = useState(null);

  // Filters
  const [search,          setSearch]          = useState("");
  const [filterCategory,  setFilterCategory]  = useState("all");
  const [filterProvider,  setFilterProvider]  = useState("all");
  const [dateFrom,        setDateFrom]        = useState("");
  const [dateTo,          setDateTo]          = useState("");

  const authHeaders = { headers: getAuthHeaders() };

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)                        params.search   = search;
      if (filterCategory !== "all")      params.category = filterCategory;
      if (filterProvider !== "all")      params.provider = filterProvider;
      if (dateFrom)                      params.date_from = dateFrom;
      if (dateTo)                        params.date_to   = dateTo;
      const res = await axios.get(`${API}/documents`, { ...authHeaders, params });
      setDocs(res.data || []);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [search, filterCategory, filterProvider, dateFrom, dateTo, getAuthHeaders]);

  const loadSummary = useCallback(async () => {
    setSumLoading(true);
    try {
      const res = await axios.get(`${API}/documents/summary`, authHeaders);
      setSummary(res.data);
    } catch {
      // non-fatal
    } finally {
      setSumLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadDocs(); },    [loadDocs]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleUploaded = () => { setUploadOpen(false); loadDocs(); loadSummary(); };
  const handleSaved    = () => { setEditDoc(null);     loadDocs(); loadSummary(); };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/documents/${deleteId}`, authHeaders);
      toast.success("Document deleted");
      loadDocs(); loadSummary();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteId(null);
    }
  };

  const handleDownload = (doc) => {
    if (!doc.file_url) { toast.error("No file URL — the document may not have uploaded to storage"); return; }
    window.open(doc.file_url, "_blank");
  };

  const handleView = (doc) => {
    if (!doc.file_url) { toast.error("No file URL available"); return; }
    window.open(doc.file_url, "_blank", "noopener");
  };

  const clearFilters = () => {
    setSearch(""); setFilterCategory("all"); setFilterProvider("all");
    setDateFrom(""); setDateTo("");
  };
  const hasFilters = search || filterCategory !== "all" || filterProvider !== "all" || dateFrom || dateTo;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Document Archive</h1>
            <p className="text-slate-500 mt-1">
              Permanent archive for bank statements, invoices, contracts, and financial records
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { loadDocs(); loadSummary(); }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setUploadOpen(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>
        </div>

        {/* Summary */}
        <SummaryStrip summary={summary} loading={sumLoading} />

        {/* Category breakdown */}
        <CategoryBreakdown summary={summary} />

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search title, description, provider, reference…"
                  className="pl-10"
                />
              </div>
              <div>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-48"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={filterProvider} onValueChange={setFilterProvider}>
                  <SelectTrigger className="w-36"><SelectValue placeholder="Provider" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    {PROVIDERS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" placeholder="From" title="Document date from" />
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" placeholder="To" title="Document date to" />
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Document list */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-14">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
              </div>
            ) : docs.length === 0 ? (
              <div className="text-center py-16">
                <Archive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">
                  {hasFilters ? "No matching documents" : "No documents yet"}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {hasFilters
                    ? "Try adjusting your filters"
                    : "Upload your first document to start building the archive"}
                </p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Document Date</TableHead>
                      <TableHead>Uploaded</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Links</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {docs.map(doc => (
                      <TableRow key={doc.id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-start gap-2.5 max-w-[260px]">
                            <div className="mt-0.5 shrink-0">{fileIcon(doc.mime_type)}</div>
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{doc.title}</p>
                              <p className="text-xs text-slate-400 truncate">{doc.file_name}</p>
                              {doc.description && (
                                <p className="text-xs text-slate-400 truncate">{doc.description}</p>
                              )}
                              {Array.isArray(doc.tags) && doc.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {doc.tags.slice(0, 3).map(t => (
                                    <span key={t} className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{t}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap ${CATEGORY_COLORS[doc.category] || "bg-slate-100 text-slate-500"}`}>
                            {doc.category}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {doc.provider || "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-slate-600">
                          <div>{fmtDate(doc.document_date)}</div>
                          {doc.period_start && doc.period_end && (
                            <div className="text-xs text-slate-400">
                              {fmtDate(doc.period_start)} – {fmtDate(doc.period_end)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-slate-500">
                          {fmtDate(doc.uploaded_at)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                          {fmtSize(doc.file_size)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5 text-xs font-mono text-slate-400">
                            {doc.related_invoice_number && <span>{doc.related_invoice_number}</span>}
                            {doc.related_booking_id     && <span>{doc.related_booking_id.slice(0,8)}</span>}
                            {doc.related_quote_number   && <span>{doc.related_quote_number}</span>}
                            {!doc.related_invoice_number && !doc.related_booking_id && !doc.related_quote_number && "—"}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            {doc.file_url && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleView(doc)} title="View">
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} title="Download">
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => setEditDoc(doc)} title="Edit metadata">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setDeleteId(doc.id)}
                              title="Delete"
                              className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="px-4 py-3 border-t border-slate-100 flex justify-between text-sm text-slate-500">
                  <span>{docs.length} {docs.length === 1 ? "document" : "documents"}</span>
                  <span>Total stored: {fmtSize(docs.reduce((s, d) => s + (d.file_size || 0), 0))}</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={handleUploaded} />
      <EditModal open={!!editDoc} onClose={() => setEditDoc(null)} onSaved={handleSaved} doc={editDoc} />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the file from cloud storage and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
